<script setup>
/* global axios */
import { ref, reactive, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const route = useRoute();
const rows = ref([]);
const proxies = ref([]);
const loading = ref(false);
const busy = ref(false);
const checkingId = ref(null);
const checkErrors = reactive({});
const denied = ref(false);
const notice = ref('');
const error = ref('');
const formError = ref('');
const dialog = ref(null);
const selected = ref(null);
const form = reactive({ name: '', bot_token: '', proxy_entry_id: '' });
let epoch = 0;
const requests = new Set();
const errors = {
  forbidden: 'Раздел доступен только администратору.',
  invalid_input: 'Проверьте название, токен и выбранную прокси. Для другого бота создайте отдельное подключение.',
  not_found: 'Бот или прокси не найдены. Обновите список.',
  duplicate: 'Этот бот уже добавлен. Повторное подключение запрещено.',
  conflict: 'Настройки изменились. Обновите список и откройте форму заново.',
  busy: 'Другая операция ещё выполняется. Повторите позже.',
  rate_limited: 'Повторная проверка доступна через 30 секунд.',
  limit_reached: 'Достигнут лимит: 100 ботов.',
  token_invalid: 'Telegram отклонил токен. Проверьте его в BotFather.',
  telegram_rate_limit: 'Telegram ограничил частоту запросов. Повторите позже.',
  telegram_unavailable: 'Telegram недоступен через выбранную прокси.',
  proxy_unavailable: 'Не удалось подключиться через прокси. Прямое соединение не использовалось.',
  proxy_auth_failed: 'Прокси отклонила авторизацию.',
  tls_failed: 'Не удалось проверить защищённое соединение с Telegram.',
  timeout: 'Истекло время ожидания. Прямое соединение не использовалось.',
  invalid_response: 'Неожиданный ответ Telegram. Подключение не подтверждено.',
  credentials_unavailable: 'Не удалось прочитать сохранённые реквизиты. Обратитесь к администратору.',
};
const statusLabel = status => ({ unchecked: 'Не проверен', checking: 'Проверка не завершена', ok: 'Связь проверена', error: 'Ошибка подключения' }[status] || 'Не проверен');
const rowStatus = bot => checkingId.value === bot.id ? 'pending' : checkErrors[bot.id] ? 'error' : bot.status;
const statusClass = bot => ({ ok: 'bg-n-teal-3 text-n-teal-11', error: 'bg-n-ruby-3 text-n-ruby-11', pending: 'bg-n-blue-3 text-n-blue-11' }[rowStatus(bot)] || 'bg-n-slate-3 text-n-slate-11');
const statusIcon = bot => ({ ok: 'i-lucide-circle-check', error: 'i-lucide-circle-alert' }[rowStatus(bot)] || 'i-lucide-circle-help');
const valid = computed(() => form.name.trim().length > 0 && form.name.trim().length <= 80 &&
  !!form.proxy_entry_id && (selected.value && !form.bot_token || /^[1-9]\d{5,14}:[A-Za-z0-9_-]{35}$/.test(form.bot_token)));

async function request(path = '', method = 'get', data) {
  const generation = epoch;
  const controller = new AbortController();
  requests.add(controller);
  try {
    const response = await axios({ url: `/api/v1/accounts/${route.params.accountId}/tg_bots${path}`,
      method, data, signal: controller.signal, timeout: 15000 });
    return response.data;
  } catch (e) {
    if (generation === epoch && [401, 403].includes(e.response?.status)) {
      denied.value = true; rows.value = []; proxies.value = []; dialog.value?.close();
    }
    // Never display raw transport exceptions: they may contain submitted credentials.
    throw new Error(errors[e.response?.data?.error] || 'Не удалось выполнить запрос. Обновите список перед повтором.');
  } finally { requests.delete(controller); }
}
function resetForm() { form.bot_token = ''; form.name = ''; form.proxy_entry_id = ''; formError.value = ''; }
async function load() {
  const generation = epoch;
  loading.value = true; error.value = ''; denied.value = false;
  try {
    const data = await request();
    if (generation !== epoch) return;
    rows.value = data.items; proxies.value = data.proxies;
  } catch (e) { if (generation === epoch) error.value = e.message; }
  finally { if (generation === epoch) loading.value = false; }
}
async function edit(bot = null) {
  selected.value = bot; resetForm();
  form.name = bot?.local_name ?? bot?.name ?? '';
  form.proxy_entry_id = bot?.proxy_entry_id || '';
  await nextTick(); dialog.value.open();
}
async function save() {
  if (!valid.value || busy.value) return;
  const generation = epoch;
  busy.value = true; formError.value = '';
  const payload = { name: form.name.trim(), proxy_entry_id: form.proxy_entry_id,
    token_mode: form.bot_token ? 'replace' : 'keep', revision: selected.value?.revision };
  if (form.bot_token) payload.bot_token = form.bot_token;
  try {
    await request(selected.value ? `/${selected.value.id}` : '', selected.value ? 'patch' : 'post', payload);
    if (generation !== epoch) return;
    dialog.value.close(); notice.value = 'Бот сохранён. Нажмите «Проверить связь». Режим получения задаётся отдельно в «ТГ группы».';
    await load();
  } catch (e) { if (generation === epoch) formError.value = e.message; }
  finally { payload.bot_token = ''; form.bot_token = ''; if (generation === epoch) busy.value = false; }
}
async function check(bot) {
  if (busy.value) return;
  const generation = epoch; busy.value = true; error.value = ''; notice.value = '';
  checkingId.value = bot.id; delete checkErrors[bot.id];
  try {
    const updated = await request(`/${bot.id}/check`, 'post');
    if (generation !== epoch) return;
    rows.value = rows.value.map(row => row.id === bot.id ? updated : row);
    notice.value = updated.status === 'ok' ? `Бот ${updated.username ? '@' + updated.username : updated.name} доступен через прокси. Режимы групп не изменены.` : '';
  } catch (e) { if (generation === epoch) checkErrors[bot.id] = e.message; }
  finally { if (generation === epoch) { busy.value = false; checkingId.value = null; } }
}
function cancelRequests() {
  epoch += 1; requests.forEach(controller => controller.abort()); requests.clear(); resetForm();
  checkingId.value = null; Object.keys(checkErrors).forEach(id => delete checkErrors[id]);
}
watch(() => route.params.accountId, () => {
  cancelRequests(); dialog.value?.close(); rows.value = []; proxies.value = []; busy.value = false; notice.value = ''; load();
}, { immediate: true });
onBeforeUnmount(cancelRequests);
</script>

<template>
  <SettingsLayout :is-loading="loading" loading-message="Загружаем подключения…">
    <template #header>
      <BaseSettingsHeader title="ТГ Бот" description="Подключение Telegram-ботов через прокси. Настройки и состояние связи — в одном месте.">
        <template #actions>
          <Button label="Обновить" slate faded :disabled="loading || busy" @click="load" />
          <Button icon="i-lucide-circle-plus" label="Добавить бота" :disabled="loading || busy || denied" @click="edit()" />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #preBody>
      <p v-if="error" role="alert" class="mb-4 text-n-ruby-11">{{ error }}</p>
      <Banner v-if="checkingId !== null" color="blue" role="status" aria-live="polite" class="mb-4">
        <span class="flex items-center gap-2"><Spinner :size="16" aria-hidden="true" />Проверяем связь с Telegram через прокси…</span>
      </Banner>
      <Banner v-else-if="notice" color="teal" role="status" aria-live="polite" class="mb-4">
        <span class="flex items-center gap-2"><Icon icon="i-lucide-circle-check" aria-hidden="true" />{{ notice }}</span>
      </Banner>
    </template>
    <template #body>
      <template v-if="!denied && !error">
        <p class="mb-6 text-sm text-n-slate-11">Сохранение и проверка не включают получение сообщений. Режим задаётся отдельно в «ТГ группы». Модерация пока недоступна. Прямого подключения без прокси нет.</p>
        <p v-if="!rows.length" class="flex items-center justify-center py-20 text-base text-n-slate-12">Боты не подключены. Нажмите «Добавить бота».</p>
        <div v-else class="divide-y divide-n-weak">
          <article v-for="bot in rows" :key="bot.id" class="grid grid-cols-1 gap-4 py-5 md:grid-cols-3">
            <div class="min-w-0">
              <h2 class="text-base font-medium break-words text-n-slate-12">{{ bot.name }}</h2>
              <p class="text-sm text-n-slate-11">{{ bot.username ? `@${bot.username}` : 'Имя в Telegram появится после проверки' }}</p>
              <p class="text-sm text-n-slate-11">Прокси: {{ proxies.find(proxy => proxy.id === bot.proxy_entry_id)?.name || 'Недоступна' }}</p>
            </div>
            <div class="min-w-0 text-sm">
              <span role="status" aria-live="polite" class="inline-flex items-center gap-2 rounded-lg px-3 py-2 mb-2 font-medium" :class="statusClass(bot)">
                <Spinner v-if="rowStatus(bot) === 'pending'" :size="16" aria-hidden="true" />
                <Icon v-else :icon="statusIcon(bot)" aria-hidden="true" />
                {{ rowStatus(bot) === 'pending' ? 'Проверяем…' : checkErrors[bot.id] ? 'Проверка не выполнена' : statusLabel(bot.status) }}
              </span>
              <p class="text-n-slate-11">Получение сообщений: управление в «ТГ группы»</p>
              <p v-if="bot.checked_at" class="text-n-slate-11">Проверка: {{ new Date(bot.checked_at).toLocaleString('ru-RU') }}</p>
              <p v-if="checkingId !== bot.id && (checkErrors[bot.id] || bot.error_code)" role="alert" class="mt-2 text-n-ruby-11">{{ checkErrors[bot.id] || errors[bot.error_code] || 'Проверка не пройдена' }}</p>
            </div>
            <div class="flex flex-wrap items-start gap-2 md:justify-end">
              <Button :label="checkingId === bot.id ? 'Проверяем…' : 'Проверить связь'" :is-loading="checkingId === bot.id" slate faded :disabled="busy" @click="check(bot)" />
              <Button label="Настроить" slate faded :disabled="busy" @click="edit(bot)" />
            </div>
          </article>
        </div>
      </template>
    </template>
    <Dialog ref="dialog" type="edit" :title="selected ? 'Настроить бота' : 'Добавить Telegram-бота'" :show-cancel-button="false" :show-confirm-button="false" @close="resetForm">
      <form class="flex flex-col gap-4" @submit.prevent="save">
        <Input id="tg-bot-name" v-model="form.name" label="Внутреннее название" placeholder="Например, Модератор групп" maxlength="80" :disabled="busy" />
        <p class="text-sm text-n-slate-11">После проверки в системе показывается имя из Telegram. Оно обновляется автоматически примерно раз в 6 часов и по кнопке «Проверить связь».</p>
        <Input id="tg-bot-token" v-model="form.bot_token" type="password" autocomplete="new-password" label="Токен BotFather" :placeholder="selected ? 'Оставьте пустым, чтобы сохранить токен' : 'Введите токен бота'" :disabled="busy" />
        <label for="tg-bot-proxy" class="text-sm font-medium text-n-slate-12">Прокси</label>
        <select id="tg-bot-proxy" v-model="form.proxy_entry_id" required :disabled="busy" class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand">
          <option value="" disabled>Выберите прокси</option>
          <option v-for="proxy in proxies" :key="proxy.id" :value="proxy.id">{{ proxy.name }} · SOCKS5 · {{ proxy.status === 'ok' ? 'Доступна' : 'Требует проверки' }}</option>
        </select>
        <p v-if="!proxies.length" class="text-sm text-n-slate-11">Сначала добавьте прокси в разделе «Прокси».</p>
        <p class="text-sm text-n-slate-11">Токен хранится зашифрованно и не показывается повторно. Если прокси недоступна, соединение останавливается.</p>
        <p v-if="formError" role="alert" class="text-sm text-n-ruby-11">{{ formError }}</p>
        <div class="flex justify-end gap-2 py-2">
          <Button label="Отменить" slate faded :disabled="busy" @click="dialog.close()" />
          <Button type="submit" label="Сохранить" :is-loading="busy" :disabled="!valid || busy" />
        </div>
      </form>
    </Dialog>
  </SettingsLayout>
</template>
